<template>
  <div ref="brandRef" class="brand-select">
    <a-select
      style="width: 100%"
      v-model:value="selectedBrandId"
      :placeholder="placeholder"
      :getPopupContainer="() => $refs.brandRef"
      :show-search="true"
      :virtual="true"
      :allowClear="true"
      :not-found-content="!loadingBrand ? 'No data found' : 'Loading...'"
      :filter-option="false"
      @search="handleSearch"
      @popupScroll="handlePopupScroll"
    >
      <a-select-option
        v-for="item in brandList"
        :key="item.value"
        :value="item.value"
      >
        {{ item.label }} {{ showAmazonName ?  `(${item.amazonName})` : '' }}
      </a-select-option>
    </a-select>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { brandApi } from '@/server'
import { CURRENCY_MAPPER } from '@/contants/common'
import { useUserInfoStore } from '@/storeData'
import accBonusProgramApi from '@/server/seller/accBonusProgram'
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select a brand',
  },
  /** When true, prepends an "All" option with empty value */
  showAll: {
    type: Boolean,
    default: true,
  },
  type:{
    type: String,
    default: 'brand',
  },
  showAmazonName: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const user = useUserInfoStore()
const countryCode = user?.userInfo?.countryCode || 'US'

// const brandRef = ref(null)
const brandList = ref([])
const loadingBrand = ref(false)
const hasMoreBrand = ref(true)
const paginationBrand = reactive({
  size: 10,
  current: 1,
})
const searchValueBrand = ref('')

const selectedBrandId = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const getPopupContainer = () => brandRef.value

const handleSearch = (value) => {
  searchValueBrand.value = value
  paginationBrand.current = 1
  brandList.value = []
  hasMoreBrand.value = true
  getBrandList(1, value)
}

const handlePopupScroll = (e) => {
  const { target } = e
  const scrollHeight = target.scrollHeight
  const scrollTop = target.scrollTop
  const clientHeight = target.clientHeight

  if (
    scrollHeight - scrollTop - clientHeight < 100 &&
    hasMoreBrand.value &&
    !loadingBrand.value
  ) {
    getBrandList(paginationBrand.current + 1, searchValueBrand.value)
  }
}

const isAccBonusProgram = computed(() => props.type === 'accBonusProgram')

const mapBrandOption = (item) => ({
  label:item.brandName|| item.amazonBrandName || item.amazonName ||  '',
  value: item.id ?? item.brandId,
  amazonName: item.amazonName,
})

/** 兼容多种返回结构：数组 / { data: [] } / { data: { data, total } } */
const normalizeBrandListResponse = (res) => {
  if (Array.isArray(res)) {
    return { success: true, rows: res, total: res.length }
  }
  if (res?.success === false) {
    return { success: false, rows: [], total: 0 }
  }
  const payload = res?.data ?? res
  if (Array.isArray(payload)) {
    return { success: true, rows: payload, total: payload.length }
  }
  if (payload && Array.isArray(payload.data)) {
    return {
      success: res?.success !== false,
      rows: payload.data,
      total: payload.total ?? payload.data.length,
    }
  }
  return { success: !!res?.success, rows: [], total: 0 }
}

const getBrandList = async (page, searchText) => {
  if (!hasMoreBrand.value || loadingBrand.value) return
  loadingBrand.value = true
  try {
    const params = {
      page,
      size: paginationBrand.size,
      marketplace: CURRENCY_MAPPER[countryCode].host,
      keyword: searchText || undefined,
    }

    const data = isAccBonusProgram.value
      ? await accBonusProgramApi.getProgramBrandList(params)
      : await brandApi.getAssociateBrandList(params)

    const { success, rows, total } = normalizeBrandListResponse(data)

    if (success) {
      const newBrandList = rows.map(mapBrandOption)

      if (page === 1) {
        brandList.value = newBrandList
      } else {
        brandList.value = [...brandList.value, ...newBrandList]
      }

      hasMoreBrand.value = isAccBonusProgram.value
        ? false
        : brandList.value.length < total
      paginationBrand.current = page
    } else if (page === 1) {
      brandList.value = []
      hasMoreBrand.value = false
    }

    if (props.showAll && page === 1) {
      const hasAllOption = brandList.value.some((item) => item.value === '')
      if (!hasAllOption) {
        brandList.value.unshift({
          label: 'All',
          value: '',
        })
      }
    }
  } catch (error) {
    message.error('Failed to fetch brand list')
  } finally {
    loadingBrand.value = false
  }
}

onMounted(() => {
  getBrandList(1, searchValueBrand.value)
})
</script>

<style scoped>
.brand-select {
  position: relative;
}
</style>